"use client";

import { useActionState, useCallback, useId } from "react";
import { ArrowRight, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { subscribeNewsletter } from "@/app/actions/newsletter";
import { NEWSLETTER_RECAPTCHA_ACTION, type NewsletterState } from "@/lib/newsletter";

const RECAPTCHA_SITE_KEY = (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "").trim();
const RECAPTCHA_READY = RECAPTCHA_SITE_KEY.length > 0;
const RECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;

// The reCAPTCHA script is injected on first interaction rather than on page
// load, so visitors who never touch the form do not pay for it. The promise is
// module-level so two forms on one page (or repeated focus) share one load.
let recaptchaLoad: Promise<void> | null = null;

function loadRecaptcha(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (!recaptchaLoad) {
    recaptchaLoad = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        recaptchaLoad = null;
        reject(new Error("reCAPTCHA script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaLoad;
}

async function getRecaptchaToken(): Promise<string> {
  await loadRecaptcha();
  return new Promise<string>((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: NEWSLETTER_RECAPTCHA_ACTION })
        .then(resolve, reject);
    });
  });
}

const initialState: NewsletterState = { status: "idle" };

const inputClass =
  "w-full rounded-lg border border-cb-border bg-cb-surface-2 px-4 py-3 text-base text-cb-text placeholder:text-cb-muted-2 outline-none focus:border-cb-violet focus:ring-2 focus:ring-cb-violet/15 transition-colors";

const labelClass = "block text-xs font-semibold text-cb-muted uppercase tracking-widest mb-2";

export function NewsletterForm() {
  const id = useId();

  const submit = useCallback(
    async (prev: NewsletterState, formData: FormData): Promise<NewsletterState> => {
      if (!RECAPTCHA_READY) {
        return {
          status: "error",
          message: "Signup is unavailable right now. Please try again later.",
        };
      }
      try {
        formData.set("recaptchaToken", await getRecaptchaToken());
      } catch {
        return {
          status: "error",
          message: "We couldn't verify your browser. Please reload the page and try again.",
        };
      }
      formData.set("pageUri", window.location.href);
      formData.set("pageName", document.title);
      try {
        return await subscribeNewsletter(prev, formData);
      } catch {
        // Network failure or the action threw; no server message to surface.
        return { status: "error", message: "Something went wrong. Please try again in a moment." };
      }
    },
    [],
  );

  const [state, formAction, pending] = useActionState(submit, initialState);

  if (state.status === "success") {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center gap-4"
        role="status"
        aria-live="polite"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cb-success/10 border border-cb-success/20">
          <CheckCircle className="h-6 w-6 text-cb-success" aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-cb-text">You&apos;re on the list</h3>
        <p className="text-sm text-cb-muted max-w-xs leading-relaxed">
          We&apos;ll email you when new labs, techniques, and courses drop. No spam.
        </p>
      </div>
    );
  }

  return (
    <form
      action={formAction}
      onFocus={RECAPTCHA_READY ? () => void loadRecaptcha().catch(() => {}) : undefined}
      className="relative space-y-5"
    >
      <div>
        <label htmlFor={`${id}-email`} className={labelClass}>
          Email <span className="text-cb-danger" aria-hidden="true">*</span>
        </label>
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          maxLength={254}
          placeholder="you@company.com"
          className={inputClass}
          aria-describedby={state.status === "error" ? `${id}-error` : undefined}
          aria-invalid={state.status === "error" || undefined}
        />
      </div>

      <div>
        <label htmlFor={`${id}-firstname`} className={labelClass}>
          Name <span className="normal-case tracking-normal font-normal text-cb-muted-2">(optional)</span>
        </label>
        <input
          id={`${id}-firstname`}
          name="firstname"
          type="text"
          autoComplete="given-name"
          maxLength={100}
          placeholder="Your name"
          className={inputClass}
        />
      </div>

      {/* Honeypot: visually removed and skipped by assistive tech; bots that
          fill every field trip the server-side check. */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input id={`${id}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      {state.status === "error" && (
        <div
          id={`${id}-error`}
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-cb-danger/20 bg-cb-danger/5 px-4 py-3"
        >
          <AlertCircle className="h-4 w-4 text-cb-danger shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-cb-danger leading-relaxed">{state.message}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={pending || !RECAPTCHA_READY}
        className="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-cb-violet px-6 py-3 text-sm font-medium text-white transition-all duration-150 hover:bg-cb-violet-dim active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
      >
        {pending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Subscribing
          </>
        ) : (
          <>
            Subscribe
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>

      <p className="text-xs text-cb-muted-2 leading-relaxed">
        Protected by reCAPTCHA. Google&apos;s{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted">
          Terms of Service
        </a>{" "}
        apply.
      </p>
    </form>
  );
}
